import API from '@/network/API'
import { CustomSuccessData } from 'axios'
import Redeem from '@/views/points/models/Redeem'
import ChannelManager from '@/utils/channel-manager'
import UserMemberInfo from '@/views/points/models/UserMemberInfo'
import PointTransaction from '@/views/points/models/PointTransaction'
import PointsRule from '@/views/points/models/PointsRule'
import LocalStorageManager from '@/utils/local-storage-manager'
// 控制是否使用假数据的配置
let USE_MOCK_DATA = false

// 提供切换真假数据的方法
export function toggleMockData(useMock?: boolean): boolean {
  if (useMock !== undefined) {
    USE_MOCK_DATA = useMock
  } else {
    USE_MOCK_DATA = !USE_MOCK_DATA
  }
  return USE_MOCK_DATA
}

// 获取当前是否使用假数据的状态
export function isMockDataEnabled(): boolean {
  return USE_MOCK_DATA
}

// 假数据
const mockData = {
  // 用户会员信息假数据
  userMemberInfo: {
    userId: '10086',
    phoneNumber: '1388888888',
    level: 2,
    totalPoints: 2500,
    bonusPoints: 500,
    basePoints: 2000,
    accumulatedPoints: 5000,
    consumeCount: 100,
    consumeAmount: 10000,
    expireConsumeCount: 10,
    expireConsumeAmount: 1000,
    totalVisit: 100
  },

  // 积分规则假数据
  pointsRule: [
    {
      level: 1,
      basePointsPer: '1%',
      bonusPointsPer: '0%',
      levelMinPoints: 0
    },
    {
      level: 2,
      basePointsPer: '1.2%',
      bonusPointsPer: '0.3%',
      levelMinPoints: 5000
    },
    {
      level: 3,
      basePointsPer: '1.5%',
      bonusPointsPer: '0.5%',
      levelMinPoints: 10000
    },
    {
      level: 4,
      basePointsPer: '2%',
      bonusPointsPer: '1%',
      levelMinPoints: 20000
    }
  ],

  // 积分交易记录假数据
  pointTransactions: {
    list: [
      {
        basePointsChange: 100,
        bonusPointsChange: -20,
        gmtCreate: 1716028800000,
        logo: '',
        orderNumber: 10001,
        orderType: 1,
        restaurantId: 1001,
        restaurantName: '测试餐厅1',
        totalPointsChange: 120,
        transType: 1,
        transactionAmount: 100
      },
      {
        transType: 2,
        basePointsChange: -200,
        bonusPointsChange: 0,
        gmtCreate: 1716028800000,
        logo: '',
        orderNumber: 10002,
        orderType: 2,
        restaurantId: 1002,
        restaurantName: '测试餐厅2',
        totalPointsChange: 0,
        transactionAmount: 0
      },
      {
        transType: 3,
        basePointsChange: 100,
        bonusPointsChange: 0,
        gmtCreate: 1716028800000,
        logo: '',
        orderNumber: 10003,
        orderType: 3,
        restaurantId: 1003,
        restaurantName: '测试餐厅3',
        totalPointsChange: 0,
        transactionAmount: 0
      },
      {
        transType: 4,
        basePointsChange: -12315,
        bonusPointsChange: 0,
        gmtCreate: 1716028800000,
        logo: '',
        orderNumber: 10004,
        orderType: 4,
        restaurantId: 1004,
        restaurantName: '测试餐厅4',
        totalPointsChange: 0,
        transactionAmount: 0
      }
    ],
    isLastPage: true,
    total: 3,
    pageNum: 1,
    pageSize: 10
  }
}

const _url = 'restaurantOrders/v1.0'
const _pointsUrl = 'restaurantPay/v1.0/app'

async function points(
  pageNum: number
): Promise<CustomSuccessData<{ isLastPage: boolean; list: Redeem[] }>> {
  const channelManager = ChannelManager.instance()
  const channel = channelManager.getChannel()

  return API.instance().get<{ isLastPage: boolean; list: Redeem[] }>(
    `${_url}/points/restaurants`,
    { pageNum, pageSize: 10, channel: channel !== 0 ? channel : undefined },
    { isSpin: pageNum === 1 }
  )
}

/**
 * 获取用户会员信息(仅circle eats渠道使用)
 * @param restaurantId - 餐厅ID
 * @returns
 */
async function getUserMemberInfo(restaurantId: string): Promise<CustomSuccessData<UserMemberInfo>> {
  // 使用假数据
  if (USE_MOCK_DATA) {
    return {
      data: mockData.userMemberInfo,
      message: '操作成功',
      status: 200
    }
  }

  // 真实API调用
  return API.instance().get<UserMemberInfo>(`${_pointsUrl}/points/userpoint/${restaurantId}`)
}

// 获取积分规则信息
async function getPointsRule(): Promise<CustomSuccessData<PointsRule[]>> {
  // 使用假数据
  if (USE_MOCK_DATA) {
    return {
      data: mockData.pointsRule as PointsRule[],
      message: '操作成功',
      status: 200
    }
  }

  // 真实API调用
  const channelManager = ChannelManager.instance()
  const channel = channelManager.getChannel()

  const isLoggedIn = new LocalStorageManager().userId.length > 0
  if (!isLoggedIn) {
    return {
      data: [],
      message: '请先登录',
      status: 401
    }
  }

  return API.instance().get<PointsRule[]>(
    `${_pointsUrl}/points/userpoint/rules`,
    { channel: channel !== 0 ? channel : undefined }
  )
}

// 获取用户积分交易记录
async function getPointTransactions(
  pageNum: number,
  pageSize = 10
): Promise<
  CustomSuccessData<{
    list: PointTransaction[]
    isLastPage: boolean
    total: number
    pageNum: number
    pageSize: number
  }>
> {
  // 使用假数据
  if (USE_MOCK_DATA) {
    return {
      data: mockData.pointTransactions as {
        list: PointTransaction[]
        isLastPage: boolean
        total: number
        pageNum: number
        pageSize: number
      },
      message: '操作成功',
      status: 200
    }
  }

  // 真实API调用
  const channelManager = ChannelManager.instance()
  const channel = channelManager.getChannel()

  // 调用接口获取交易记录
  const response = await API.instance().get<{
    endRow: number
    firstPage: number
    hasNextPage: boolean
    hasPreviousPage: boolean
    isFirstPage: boolean
    isLastPage: boolean
    lastPage: number
    list: Array<{
      basePointsChange: number
      bonusPointsChange: number
      gmtCreate: number
      logo: string
      orderNumber: number
      orderType: number
      restaurantId: number
      restaurantName: string
      totalPointsChange: number
      transType: number
      transactionAmount: number
    }>
    navigatePages: number
    navigatepageNums: number[]
    nextPage: number
    orderBy: string
    pageNum: number
    pageSize: number
    pages: number
    prePage: number
    size: number
    startRow: number
    total: number
  }>(
    `${_pointsUrl}/points/transaction/records`,
    {
      pageNum,
      pageSize,
      channel: channel !== 0 ? channel : undefined
    },
    { isSpin: pageNum === 1 }
  )

  // 将接口返回的数据转换为PointTransaction格式
  const transformedList: PointTransaction[] = response.data.list.map((item) => {
    return {
      basePointsChange: item.basePointsChange,
      bonusPointsChange: item.bonusPointsChange,
      gmtCreate: item.gmtCreate,
      logo: item.logo,
      orderNumber: item.orderNumber,
      orderType: item.orderType,
      restaurantId: item.restaurantId,
      restaurantName: item.restaurantName,
      totalPointsChange: item.totalPointsChange,
      transType: item.transType,
      transactionAmount: item.transactionAmount
    }
  })

  return {
    data: {
      list: transformedList,
      isLastPage: response.data.isLastPage,
      total: response.data.total,
      pageNum: response.data.pageNum,
      pageSize: response.data.pageSize
    },
    message: response.message,
    status: response.status
  }
}

// 导出单个函数
export { points, getPointsRule, getUserMemberInfo, getPointTransactions }

// 提供默认导出
export default {
  points,
  getPointsRule,
  getUserMemberInfo,
  getPointTransactions,
  toggleMockData,
  isMockDataEnabled
}
